// src/modules/myvault/components/automations/actions/SendEmailAction.tsx

// What? Action editor for SEND_EMAIL. Template picker + RecipientPicker +
//       optional CC + context variable mapper. Mirrors the R-3a Canvas
//       Action Button config so admins get the same mental model for both
//       user-triggered and event-triggered sends.
// Why?  SEND_EMAIL is the highest-value automation action -- it's what
//       unblocks "when salary changes, email HR" and similar workflows.
// How?  Fetches workspace-scoped templates per R-6a, groups via useGroups
//       for RecipientPicker. Context mapper is a simple key/value table of
//       template variable -> row-field expression (free text, `{{field}}`
//       refs resolved server-side).

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RecipientPicker, { RecipientEntry } from "../../RecipientPicker";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useGroups } from "../../../hooks/useGroups";
import type { ActionEditorProps } from "../types";

interface TemplateSummary {
  template_key: string;
  module: string;
  display_name: string;
  subject_template: string;
  is_system: boolean;
  available_variables?: { name: string; description: string }[];
}

const SendEmailAction: React.FC<ActionEditorProps> = ({
  config,
  onChange,
  context,
}) => {
  const authFetch = useAuthFetch();
  const { groups } = useGroups(context.workspaceId);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!context.workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await authFetch(
          `/api/email/workspace/${context.workspaceId}/templates`,
          "fetch workspace templates",
        );
        if (!cancelled && Array.isArray(data)) setTemplates(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load templates",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, context.workspaceId]);

  const template = typeof config.template === "string" ? config.template : "";
  const recipients: RecipientEntry[] = Array.isArray(config.recipients)
    ? (config.recipients as RecipientEntry[])
    : [];
  const cc: RecipientEntry[] = Array.isArray(config.cc)
    ? (config.cc as RecipientEntry[])
    : [];
  const ctx: Record<string, string> =
    typeof config.context === "object" && config.context !== null
      ? (config.context as Record<string, string>)
      : {};

  const selectedTemplate = templates.find((t) => t.template_key === template);
  const availableVars = selectedTemplate?.available_variables || [];

  const updateContext = (key: string, value: string) => {
    const next = { ...ctx };
    if (value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange({ context: next });
  };

  const removeContextKey = (key: string) => {
    const next = { ...ctx };
    delete next[key];
    onChange({ context: next });
  };

  const roleFields = context.columns
    .filter((c) => c.is_foreign_key)
    .map((c) => c.column_name);
  const recordFields = context.columns
    .filter((c) => !c.is_foreign_key)
    .map((c) => c.column_name);

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Email template</FormLabel>
        <Select
          size="sm"
          value={template}
          onChange={(_, val) => onChange({ template: (val as string) || "" })}
          placeholder={loading ? "Loading..." : "Select a template"}
          disabled={loading}
        >
          <Option value="">None</Option>
          {templates.map((t) => (
            <Option key={t.template_key} value={t.template_key}>
              {t.display_name}
            </Option>
          ))}
        </Select>
        {error && (
          <FormHelperText sx={{ color: "danger.600" }}>{error}</FormHelperText>
        )}
        {!error && (
          <FormHelperText>
            Templates come from Org Settings &rsaquo; Email Templates. Scoped to
            this workspace when the workspace has overrides.
          </FormHelperText>
        )}
      </FormControl>

      <RecipientPicker
        value={recipients}
        onChange={(next) => onChange({ recipients: next })}
        groups={groups}
        roleFields={roleFields}
        recordFields={recordFields}
      />

      <Box>
        <FormLabel sx={{ mb: 0.5 }}>CC (optional)</FormLabel>
        <RecipientPicker
          value={cc}
          onChange={(next) => onChange({ cc: next })}
          groups={groups}
          roleFields={roleFields}
          recordFields={recordFields}
        />
      </Box>

      <Box>
        <FormLabel>Context mapping</FormLabel>
        <FormHelperText>
          Map template variables to row fields. Use <code>{"{{field}}"}</code>{" "}
          refs to pull values from the triggering row; literal text is also
          allowed.
        </FormHelperText>
        {availableVars.length > 0 && (
          <Alert size="sm" color="neutral" variant="soft" sx={{ mt: 1, mb: 1 }}>
            <Typography level="body-xs">
              Available variables:{" "}
              {availableVars.map((v) => `{{ ${v.name} }}`).join(", ") ||
                "none documented"}
            </Typography>
          </Alert>
        )}
        <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 1, mt: 1 }}>
          <Stack spacing={1}>
            {Object.entries(ctx).length === 0 && (
              <Typography
                level="body-xs"
                sx={{ color: "text.tertiary", textAlign: "center", py: 1 }}
              >
                No variable mappings configured.
              </Typography>
            )}
            {Object.entries(ctx).map(([key, value]) => (
              <Stack key={key} direction="row" spacing={1} alignItems="center">
                <Input
                  size="sm"
                  value={key}
                  disabled
                  sx={{ width: 200, fontFamily: "monospace" }}
                />
                <Input
                  size="sm"
                  value={value}
                  onChange={(e) => updateContext(key, e.target.value)}
                  placeholder="{{ employee_name }} or literal"
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => removeContextKey(key)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <AddMappingRow
              onAdd={(key, value) => updateContext(key, value)}
              existingKeys={Object.keys(ctx)}
            />
          </Stack>
        </Sheet>
      </Box>
    </Stack>
  );
};

function AddMappingRow({
  onAdd,
  existingKeys,
}: {
  onAdd: (key: string, value: string) => void;
  existingKeys: string[];
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const handleAdd = () => {
    const trimmed = key.trim();
    if (!trimmed || existingKeys.includes(trimmed)) return;
    onAdd(trimmed, value);
    setKey("");
    setValue("");
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Input
        size="sm"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="variable_name"
        sx={{ width: 200, fontFamily: "monospace" }}
      />
      <Input
        size="sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="{{ field_name }}"
        sx={{ flex: 1 }}
      />
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        startDecorator={<AddIcon />}
        onClick={handleAdd}
        disabled={!key.trim() || existingKeys.includes(key.trim())}
      >
        Add
      </Button>
    </Stack>
  );
}

export default SendEmailAction;
